// W9RequestPage — /send/w9-request: staff "Request a W-9" for an owner or a
// vendor (docs/appfolio-owner-vendor-sync-spec.md §6).
//
//   • Owner  — pick from AppFolio (GET /api/owners/search), or, when they are
//              not in AppFolio yet, type a name + email (spec D3: the TIN then
//              waits until the owner has an AppFolio record).
//   • Vendor — pick from AppFolio only (GET /api/vendors/search); a vendor W-9
//              needs the vendor's record, so there is no freeform vendor.
//
// The email is prefilled from the pick and stays editable. Staff addresses are
// refused (here, and by the server as 400 staff_email). The POST's documented
// answers — 201 sent, 409 open_request_exists (offer to replace), 400, 503 —
// each render one notice (lib/ownerSync.ts w9OutcomeNotice).
//
// Opened from the Owners page as `?kind=owner&name=…&email=…`: the name and
// email land in the not-in-AppFolio fields and the email is searched, so staff
// can switch to the AppFolio record if the owner is there.
//
// Ids never reach the screen — the AppFolio owner / vendor id is a result
// row's key and a request field only (docs/no-ids-in-ui.md).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RecipientResultList } from '@/components/ownerSync/RecipientResultList';
import { isEmailValid } from '@/lib/epmaClient';
import {
  isStaffEmail,
  ownerResultRow,
  vendorResultRow,
  w9OutcomeNotice,
  w9PrefillFromSearch,
  type OwnerMatch,
  type VendorMatch,
  type W9RecipientKind,
  type W9RequestBody,
  type W9RequestOutcome,
} from '@/lib/ownerSync';
import { createW9Request, searchOwners, searchVendors } from '@/lib/ownerSyncClient';

const MIN_QUERY = 2;
const SEARCH_DEBOUNCE_MS = 300;

export function W9RequestPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  // The query string seeds the form once; later edits are the operator's.
  const prefill = useMemo(() => w9PrefillFromSearch(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps
  const cameWithOwner = prefill.kind === 'owner' && !!(prefill.name || prefill.email);

  const [kind, setKind] = useState<W9RecipientKind>(prefill.kind);
  const [query, setQuery] = useState(prefill.email || prefill.name);
  const [ownerResults, setOwnerResults] = useState<OwnerMatch[] | null>(null);
  const [vendorResults, setVendorResults] = useState<VendorMatch[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [selectedOwner, setSelectedOwner] = useState<OwnerMatch | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<VendorMatch | null>(null);
  /** Owner only: not in AppFolio yet → type the name + email. */
  const [freeform, setFreeform] = useState(cameWithOwner);
  const [name, setName] = useState(prefill.kind === 'owner' ? prefill.name : '');
  const [email, setEmail] = useState(prefill.kind === 'owner' ? prefill.email : '');

  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<W9RequestOutcome | null>(null);

  // --- debounced search (the newest query wins; a late reply is dropped) ---
  const searchSeq = useRef(0);
  useEffect(() => {
    const q = query.trim();
    const seq = ++searchSeq.current;
    if (q.length < MIN_QUERY) {
      setOwnerResults(null);
      setVendorResults(null);
      setSearching(false);
      setSearchError(null);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      void (async () => {
        try {
          if (kind === 'owner') {
            const rows = await searchOwners(q);
            if (seq === searchSeq.current) setOwnerResults(rows);
          } else {
            const rows = await searchVendors(q);
            if (seq === searchSeq.current) setVendorResults(rows);
          }
          if (seq === searchSeq.current) setSearchError(null);
        } catch {
          if (seq === searchSeq.current) {
            setSearchError(
              kind === 'owner'
                ? 'Could not search AppFolio owners just now. Try again in a moment.'
                : 'Could not search AppFolio vendors just now. Try again in a moment.',
            );
          }
        } finally {
          if (seq === searchSeq.current) setSearching(false);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [kind, query]);

  const switchKind = (next: W9RecipientKind) => {
    if (next === kind) return;
    setKind(next);
    setSelectedOwner(null);
    setSelectedVendor(null);
    setOwnerResults(null);
    setVendorResults(null);
    setFreeform(false);
    setName('');
    setEmail('');
    setOutcome(null);
  };

  const pickOwner = (key: string) => {
    const m = ownerResults?.find((o) => o.appfolioOwnerId === key);
    if (!m) return;
    setSelectedOwner(m);
    setFreeform(false);
    setEmail(m.primaryEmail ?? '');
    setOutcome(null);
  };

  const pickVendor = (key: string) => {
    const v = vendorResults?.find((o) => o.appfolioVendorId === key);
    if (!v) return;
    setSelectedVendor(v);
    setEmail(v.primaryEmail ?? '');
    setOutcome(null);
  };

  const clearPick = () => {
    setSelectedOwner(null);
    setSelectedVendor(null);
    setEmail('');
    setOutcome(null);
  };

  const startFreeform = () => {
    setSelectedOwner(null);
    setFreeform(true);
    setOutcome(null);
  };

  // --- validity ---
  const recipientName =
    kind === 'vendor'
      ? (selectedVendor?.displayName ?? '')
      : selectedOwner
        ? selectedOwner.displayName
        : freeform
          ? name.trim()
          : '';
  const hasRecipient = kind === 'vendor' ? !!selectedVendor : !!selectedOwner || freeform;
  const emailTrim = email.trim();
  const emailBad = emailTrim.length > 0 && !isEmailValid(emailTrim);
  const emailStaff = emailTrim.length > 0 && isStaffEmail(emailTrim);
  const canSubmit =
    !busy && hasRecipient && !!recipientName && isEmailValid(emailTrim) && !emailStaff;

  const submit = async (replaceOpen: boolean) => {
    if (!canSubmit) return;
    const body: W9RequestBody =
      kind === 'vendor' && selectedVendor
        ? {
            recipientKind: 'vendor',
            appfolioId: selectedVendor.appfolioVendorId,
            displayName: selectedVendor.displayName,
            isCompany: selectedVendor.isCompany,
            email: emailTrim,
          }
        : selectedOwner
          ? {
              recipientKind: 'owner',
              appfolioId: selectedOwner.appfolioOwnerId,
              displayName: selectedOwner.displayName,
              isCompany: selectedOwner.isCompany,
              email: emailTrim,
            }
          : { recipientKind: 'owner', displayName: name.trim(), email: emailTrim };
    if (replaceOpen) body.replaceOpen = true;
    setBusy(true);
    try {
      setOutcome(await createW9Request(body));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setOutcome(null);
    setSelectedOwner(null);
    setSelectedVendor(null);
    setFreeform(false);
    setName('');
    setEmail('');
    setQuery('');
  };

  const header = (
    <div style={{ marginBottom: 'var(--space-6)' }}>
      <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
        Request a W-9
      </h1>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 'var(--space-2) 0 0' }}>
        Email an owner or a vendor a secure link to fill in and sign their Form W-9. Once it is
        signed, the taxpayer ID goes onto their AppFolio record.
      </p>
    </div>
  );

  // --- sent: success state ---
  if (outcome?.kind === 'sent') {
    const notice = w9OutcomeNotice(outcome, kind);
    return (
      <div style={{ maxWidth: 760 }}>
        {header}
        <div className="alert" data-tone="success" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            <div className="alert-title">{notice.title}</div>
            {notice.body}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <button type="button" className="btn" data-variant="secondary" onClick={reset}>
            Send another W-9 request
          </button>
          <Link className="btn" data-variant="ghost" to="/requests">
            Go to Requests
          </Link>
        </div>
      </div>
    );
  }

  const notice = outcome ? w9OutcomeNotice(outcome, kind) : null;
  const ownerRows = (ownerResults ?? []).map(ownerResultRow);
  const vendorRows = (vendorResults ?? []).map(vendorResultRow);
  const showResults = query.trim().length >= MIN_QUERY && !searchError;

  return (
    <div style={{ maxWidth: 760 }}>
      {header}

      <div className="stack">
        {/* Who */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Who is it for?</h3>
          </div>
          <div className="stack-sm">
            <div className="tabs" data-variant="segmented" role="tablist" aria-label="Recipient type">
              {(['owner', 'vendor'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  className="tab"
                  role="tab"
                  aria-selected={kind === k}
                  onClick={() => switchKind(k)}
                >
                  {k === 'owner' ? 'Owner' : 'Vendor'}
                </button>
              ))}
            </div>

            {cameWithOwner && kind === 'owner' && !selectedOwner && (
              <div className="alert" data-tone="info" role="note">
                <div className="alert-body">
                  Opened for {prefill.name || prefill.email}. If they are already in AppFolio,
                  choose them below so the W-9 lands on their AppFolio record; otherwise send it as
                  entered.
                </div>
              </div>
            )}

            <div className="field">
              <label htmlFor="w9-search">
                {kind === 'owner' ? 'Find the owner in AppFolio' : 'Find the vendor in AppFolio'}
              </label>
              <input
                id="w9-search"
                type="search"
                className="input"
                placeholder={kind === 'owner' ? 'Name, company or email' : 'Vendor name, company or email'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
              <span className="helper">
                {searching ? 'Searching…' : `Type at least ${MIN_QUERY} characters.`}
              </span>
            </div>

            {searchError && (
              <div className="alert" data-tone="warning" role="note">
                <div className="alert-body">{searchError}</div>
              </div>
            )}

            {showResults && kind === 'owner' && ownerResults !== null && (
              <RecipientResultList
                rows={ownerRows}
                selectedKey={selectedOwner?.appfolioOwnerId ?? null}
                onPick={pickOwner}
                emptyText="No AppFolio owners match. If they are not in AppFolio yet, enter their name and email below."
              />
            )}
            {showResults && kind === 'vendor' && vendorResults !== null && (
              <RecipientResultList
                rows={vendorRows}
                selectedKey={selectedVendor?.appfolioVendorId ?? null}
                onPick={pickVendor}
                emptyText="No AppFolio vendors match. A vendor has to be in AppFolio before we can request their W-9."
              />
            )}

            {kind === 'owner' && !freeform && (
              <button
                type="button"
                className="btn"
                data-variant="link"
                style={{ alignSelf: 'flex-start', padding: 0 }}
                onClick={startFreeform}
              >
                Not in AppFolio yet? Enter their name and email
              </button>
            )}
          </div>
        </section>

        {/* Recipient + email */}
        {hasRecipient && (
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Send to</h3>
            </div>
            <div className="stack-sm">
              {kind === 'owner' && freeform && !selectedOwner ? (
                <>
                  <div className="field">
                    <label htmlFor="w9-name">Owner name (as it should appear on the request)</label>
                    <input
                      id="w9-name"
                      type="text"
                      className="input"
                      placeholder="Jane Doe or Maple Holdings, LLC"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      aria-invalid={!name.trim()}
                    />
                  </div>
                  <p className="oc-meta" style={{ margin: 0 }}>
                    This owner is not in AppFolio yet. The W-9 goes out now, and its taxpayer ID is
                    added to AppFolio once the owner has an AppFolio record.
                  </p>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <span>
                    <strong>{recipientName}</strong>
                    <span className="oc-meta"> · from AppFolio</span>
                  </span>
                  <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={clearPick}>
                    Change
                  </button>
                </div>
              )}
              <div className="field">
                <label htmlFor="w9-email">Email for the W-9 link</label>
                <input
                  id="w9-email"
                  type="email"
                  className="input"
                  placeholder={kind === 'owner' ? 'owner@example.com' : 'vendor@example.com'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!emailTrim || emailBad || emailStaff}
                />
                {!emailTrim && (
                  <span className="helper" data-state="error">
                    {kind === 'vendor' && selectedVendor
                      ? 'AppFolio has no email for this vendor — enter one to send.'
                      : 'Email required to send.'}
                  </span>
                )}
                {emailBad && <span className="helper" data-state="error">Enter a valid email.</span>}
                {emailStaff && (
                  <span className="helper" data-state="error">
                    That is a Welcome Home Properties address. Use the {kind}&apos;s own email.
                  </span>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Outcome of the last attempt (anything but "sent") */}
        {notice && (
          <div className="alert" data-tone={notice.tone} role="status">
            <div className="alert-body">
              <div className="alert-title">{notice.title}</div>
              {notice.body}
              {notice.offerReplace && (
                <div style={{ marginTop: 'var(--space-3)' }}>
                  <button
                    type="button"
                    className="btn"
                    data-variant="secondary"
                    data-size="sm"
                    disabled={!canSubmit}
                    onClick={() => void submit(true)}
                  >
                    {busy ? 'Sending…' : 'Send a new one — this cancels the open request'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <section className="card">
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {kind === 'vendor'
                ? 'Emails the vendor a secure link to their W-9. When they sign, the taxpayer ID is set on their AppFolio vendor record.'
                : 'Emails the owner a secure link to their W-9. When they sign, the taxpayer ID is set on their AppFolio owner record and the signed W-9 is attached to it.'}
            </p>
            <button
              type="button"
              className="btn"
              disabled={!canSubmit}
              onClick={() => void submit(false)}
              style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
            >
              {busy ? 'Sending…' : recipientName ? `Send W-9 request to ${recipientName} →` : 'Send W-9 request →'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
